/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { ContextItem, PersistedContextItem } from '@squiz/dxp-ai-types';
import { Toast, TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS } from '@squiz/sds';
import { useToastStore } from 'dxpConsoleCore-mf/State';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AI_STRINGS } from '../../../../../Constants/Strings.constants';
import { getAiService } from '../GetAiService/getAiService';
import { UseContextItemPersisterProps, UseContextItemPersisterResult } from './useContextItemPersister.types';

export const useContextItemPersister = (props: UseContextItemPersisterProps): UseContextItemPersisterResult => {
  const { ai, contextId, contextItem, onSaveSuccess, onSaveError } = props;
  const [draft, setDraft] = useState<ContextItem | PersistedContextItem>(contextItem || { data: {} });
  const [isSaving, setIsSaving] = useState(false);
  const aiService = getAiService();
  const { updateActiveToast } = useToastStore();
  const { t } = useTranslation();
  const labels = useMemo(
    () => ({
      changesSaved: t(AI_STRINGS.changesSaved),
      contextItemCreated: t(AI_STRINGS.contextItemCreated),
      failedToCreateOrUpdateContextItem: t(AI_STRINGS.failedToCreateOrUpdateContextItem),
      youHaveSuccessfullyCreatedContextItem: (contextItemId: string): string =>
        t(AI_STRINGS.youHaveSuccessfullyCreatedContextItem, { contextItemId }),
    }),
    [t],
  );
  const save = useCallback(async (): Promise<void> => {
    setIsSaving(true);

    try {
      const isExistingContextItem = draft && 'id' in draft;
      const updated = isExistingContextItem
        ? await aiService.apiClient.chatbotContextItem.update(ai.id, contextId, draft.id, draft)
        : await aiService.apiClient.chatbotContextItem.create(ai.id, contextId, draft);

      setDraft(updated);
      setIsSaving(false);
      onSaveSuccess();
      updateActiveToast(
        <Toast
          showToast
          stateHandler={useToastStore}
          title={isExistingContextItem ? labels.changesSaved : labels.contextItemCreated}
          toastContent={
            isExistingContextItem ? undefined : <>{labels.youHaveSuccessfullyCreatedContextItem(updated.id)}</>
          }
          toastType={TOAST_TYPE_SUCCESS}
        />,
      );
    } catch (error) {
      setIsSaving(false);
      onSaveError(error);
      updateActiveToast(
        <Toast
          showToast
          stateHandler={useToastStore}
          title={labels.failedToCreateOrUpdateContextItem}
          toastType={TOAST_TYPE_DANGER}
        />,
      );
    }
  }, [ai, aiService.apiClient, contextId, draft, labels, onSaveError, onSaveSuccess, updateActiveToast]);

  return {
    draft,
    isSaving,
    save,
    setDraft,
  };
};
